import React, { useState } from 'react'
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text as RNText,
  TouchableOpacity,
  View,
} from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { EventType } from '@hooks'
import { getCommonStyles, Scope } from '@utils'
import { MaterialIcons } from '@expo/vector-icons'
import { Button, DeleteModal, EventParticipants } from '../molecules'
import { Icon, Text } from '../atoms'
import { getOrganismsStyles } from './style'
import { useLanguage } from '@context'
import {
  CalendarEvent,
  useCreationStackIntegration,
  useDeleteCalendarEvent,
  usePaginateCalendarEvents,
  usePaginateKeepContacts,
  useScreen,
  useUpdateCalendarEvent,
} from '@hooks'
import dayjs from 'dayjs'

type EventStatus = 'overdue' | 'today' | 'upcoming'

const getEventStatus = (dateStr: string): EventStatus => {
  const now = dayjs()
  const date = dayjs(dateStr)
  if (date.isSame(now, 'day')) return 'today'
  if (date.isBefore(now, 'day')) return 'overdue'
  return 'upcoming'
}

export const Reminders = ({
  data,
  selectedContactId,
  contactAccountId,
  birthDate,
  withAddButton = false,
  isGhost = false,
  onEventPress,
}: {
  data?: CalendarEvent[]
  selectedContactId?: string
  contactAccountId?: string
  birthDate?: string
  withAddButton?: boolean
  isGhost?: boolean
  onEventPress?: (event: CalendarEvent) => void
}) => {
  const navigation = useNavigation()
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const organismsStyles = getOrganismsStyles(theme)
  const { isMobile } = useScreen()
  const { isCreationStackEnabled, showReminderCreation, showKeepContactCreation } =
    useCreationStackIntegration()

  const { flatData: keepContactsForContact } = usePaginateKeepContacts({ contactAccountId })

  const existingKeepContact =
    (keepContactsForContact ?? []).find((k) => k.contactAccountId === contactAccountId) ?? null

  const goToKeepContact = () => {
    if (isCreationStackEnabled) {
      showKeepContactCreation({
        isCreationStack: true,
        keepContactId: existingKeepContact?.id,
        contactId: contactAccountId,
      })
    } else {
      ;(navigation as any).navigate('Menu', {
        screen: 'KeepContact',
        params: {
          screen: 'AddKeep',
          params: {
            contactId: contactAccountId,
            keepContactId: existingKeepContact?.id,
          },
        },
      })
    }
  }

  const birthdayLabel = birthDate
    ? `Anniversaire: ${dayjs(birthDate, 'YYYY-MM-DD').format('D MMMM')}`
    : null

  const showInfoBanner = withAddButton

  const { flatData: allEvents } = usePaginateCalendarEvents()
  const events =
    data ||
    allEvents.filter(
      (event) => !selectedContactId || event.participantAccountIds.includes(selectedContactId),
    )

  const [selectedTab, setSelectedTab] = useState<EventType>(
    isGhost ? EventType.PRIVATE : EventType.COMMON,
  )
  const filteredEvents = events.filter(
    (event) => event.type === selectedTab || selectedTab === EventType.ALL,
  )

  const { mutate: deleteCalendarEvent } = useDeleteCalendarEvent()
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [eventIdToDelete, setEventIdToDelete] = useState<string | null>(null)

  const statusColors: Record<EventStatus, string> = {
    overdue: theme.colors.error,
    today: '#FF9500',
    upcoming: theme.colors.green,
  }

  const statusLabels: Record<EventStatus, string> = {
    overdue: t('common.statusOverdue'),
    today: t('common.today'),
    upcoming: t('common.statusUpcoming'),
  }

  const { mutate: updateCalendarEvent } = useUpdateCalendarEvent()
  const [localDoneIds, setLocalDoneIds] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    ;(data || []).forEach((e) => {
      if (e.isDone) initial.add(e.id)
    })
    return initial
  })

  const handleToggleDone = (event: CalendarEvent) => {
    const isCurrentlyDone = localDoneIds.has(event.id) || !!event.isDone
    setLocalDoneIds((prev) => {
      const next = new Set(prev)
      if (isCurrentlyDone) next.delete(event.id)
      else next.add(event.id)
      return next
    })
    updateCalendarEvent({ id: event.id, isDone: !isCurrentlyDone })
  }

  const showDeleteModal = (eventId: string) => {
    setEventIdToDelete(eventId)
    setIsDeleteModalVisible(true)
  }

  const handleDeleteEvent = () => {
    if (eventIdToDelete) {
      deleteCalendarEvent({ id: eventIdToDelete })
      setEventIdToDelete(null)
      setIsDeleteModalVisible(false)
    }
  }

  const goToAddReminder = () => {
    if (isCreationStackEnabled) {
      showReminderCreation({
        isCreationStack: true,
        contactId: selectedContactId,
      })
    } else {
      ;(navigation as any).navigate('Menu', {
        screen: 'Calendar',
        params: {
          screen: 'AddEvent',
          params: {
            contactId: selectedContactId,
          },
        },
      })
    }
  }

  const handleEditEvent = (event: CalendarEvent) => {
    if (onEventPress) {
      onEventPress(event)
    } else {
      ;(navigation as any).navigate('Menu', {
        screen: 'Calendar',
        params: {
          screen: 'AddEvent',
          params: { eventId: event.id, contactId: selectedContactId },
        },
      })
    }
  }

  const renderEvent = (event: CalendarEvent) => {
    const status = getEventStatus(event.startDateTime)
    const isDone = localDoneIds.has(event.id) || !!event.isDone
    const statusColor = isDone ? theme.colors.green : statusColors[status]

    return (
      <View key={event.id} style={[styles.eventCard, isDone && styles.eventCardDone]}>
        <TouchableOpacity
          style={styles.eventTouchable}
          onPress={() => handleEditEvent(event)}
          activeOpacity={0.7}
        >
          <View style={styles.eventInfo}>
            <RNText style={[styles.eventTitle, isDone && styles.eventTitleDone]} numberOfLines={1}>
              {event.title}
            </RNText>
            {!!event.description && (
              <RNText style={styles.eventDetailText} numberOfLines={1}>
                {event.description}
              </RNText>
            )}
            <View style={styles.eventDetails}>
              <EventParticipants participantAccountIds={event.participantAccountIds} />
              <View style={commonStyles.row}>
                <View style={styles.eventDetailRow}>
                  <MaterialIcons name="access-time" size={14} color="#8E8E93" />
                  <RNText style={styles.eventDetailText}>
                    {dayjs(event.startDateTime).format('DD/MM/YYYY HH:mm')}
                  </RNText>
                </View>
                {!!event.location?.place && (
                  <View style={[styles.eventDetailRow, commonStyles.flex]}>
                    <MaterialIcons name="location-on" size={14} color="#8E8E93" />
                    <RNText style={styles.eventDetailText} numberOfLines={1}>
                      {event.location.place}
                    </RNText>
                  </View>
                )}
              </View>
            </View>
          </View>
        </TouchableOpacity>
        <View style={!isMobile && commonStyles.row}>
          <View
            style={[
              styles.eventBadge,
              { borderColor: statusColor },
              isMobile && { alignSelf: 'flex-end', marginBottom: theme.spacing.xs },
            ]}
          >
            <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
            <RNText style={[styles.eventBadgeText, { color: statusColor }]}>
              {isDone ? t('common.statusDone') : statusLabels[status]}
            </RNText>
          </View>
          <View style={[styles.eventActions, isMobile && commonStyles.alignSelfEnd]}>
            <TouchableOpacity
              style={styles.eventIconButton}
              onPress={() => handleToggleDone(event)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel={t(
                isDone ? 'calendarEvent.markAsNotDone' : 'calendarEvent.markAsDone',
              )}
              testID="reminder-toggle-done"
            >
              <MaterialIcons
                name={isDone ? 'check-circle' : 'radio-button-unchecked'}
                size={22}
                color={isDone ? theme.colors.green : theme.colors.lightGrey}
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.eventIconButton}
              onPress={() => handleEditEvent(event)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Icon name="editPen" size={theme.iconSize.small} color={theme.colors.greyBlue} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.eventIconButton}
              onPress={() => showDeleteModal(event.id)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Icon name="trash" size={theme.iconSize.small} color={theme.colors.error} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    )
  }

  return (
    <View>
      {showInfoBanner && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[!isMobile && styles.marginHorizontal]}
          contentContainerStyle={styles.notification}
        >
          <TouchableOpacity style={commonStyles.row} onPress={goToKeepContact}>
            <Icon
              name="keepContact"
              color={theme.colors.primary}
              size={theme.iconSize.extraSmall}
            />
            <Text type="bodySmall" color={theme.colors.primary}>
              {existingKeepContact
                ? `Garder contact: Rappel prévu le ${dayjs(existingKeepContact.keepDate).format('D MMMM')}`
                : 'Garder contact'}
            </Text>
          </TouchableOpacity>
          {birthdayLabel && (
            <TouchableOpacity
              style={commonStyles.row}
              onPress={() =>
                (navigation as any).navigate('Menu', {
                  screen: 'Birthdays',
                  params: { contactId: selectedContactId },
                })
              }
            >
              <Icon
                name="calendarStar"
                color={theme.colors.primary}
                size={theme.iconSize.extraSmall}
              />
              <Text type="bodySmall" color={theme.colors.primary}>
                {birthdayLabel}
              </Text>
            </TouchableOpacity>
          )}
        </ScrollView>
      )}
      <View style={styles.reminderContainer}>
        {/* Event Type Tabs */}
        <View style={styles.row}>
          {!isGhost && (
            <TouchableOpacity
              testID="reminder-tab-shared"
              onPress={() => setSelectedTab(EventType.COMMON)}
              style={[
                styles.row,
                {
                  borderBottomWidth: 2,
                  borderBottomColor:
                    selectedTab === EventType.COMMON ? theme.colors.primary : theme.colors.grey20,
                },
              ]}
            >
              <Icon
                name="user"
                size={theme.iconSize.small}
                color={selectedTab === EventType.COMMON ? theme.colors.primary : theme.colors.greyBlue}
              />
              <Text type="bodyBold">
                {t('calendarEvent.sharedCount', {
                  count: events.filter((n) => n.type === EventType.COMMON).length,
                })}
              </Text>
              <Text type="body" color={theme.colors.lightGrey}>
                {t('common.parenCount', {
                  count: events.filter((n) => n.type === EventType.COMMON).length,
                })}
              </Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            testID="reminder-tab-personal"
            onPress={() => setSelectedTab(EventType.PRIVATE)}
            style={[
              styles.row,
              !isGhost && {
                borderBottomWidth: 2,
                borderBottomColor:
                  selectedTab === EventType.COMMON ? theme.colors.grey10 : theme.colors.primary,
              },
            ]}
          >
            <Icon
              name="lock"
              size={theme.iconSize.small}
              color={selectedTab !== EventType.COMMON ? theme.colors.primary : theme.colors.greyBlue}
            />
            <Text type="bodyBold">{t('calendarEvent.personal')}</Text>
            <Text type="body" color={theme.colors.lightGrey}>
              {t('common.parenCount', {
                count: events.filter((n) => n.type === EventType.PRIVATE).length,
              })}
            </Text>
          </TouchableOpacity>
        </View>

        {withAddButton && (
          <Button
            isSmall
            style={[
              organismsStyles.buttonAddWithoutMargin,
              !isMobile && commonStyles.alignSelfCenter,
            ]}
            title={t('calendarEvent.createEvent')}
            textColor={theme.colors.primary}
            isIconRight
            iconName="plus"
            iconColor={theme.colors.primary}
            onPress={goToAddReminder}
          />
        )}
        {/* Events List */}
        <View style={styles.eventsContainer}>{filteredEvents.map(renderEvent)}</View>
      </View>
      <DeleteModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        title={t('calendarEvent.deleteConfirm')}
        onDelete={handleDeleteEvent}
      />
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    reminderContainer: {
      padding: theme.spacing.lg,
      gap: theme.spacing.sm,
    },
    row: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.xs,
      paddingBottom: theme.spacing.sm,
    },
    notification: {
      flexGrow: 1,
      justifyContent: 'space-around',
      gap: theme.spacing.lg,
      backgroundColor: theme.colors.primary + '10',
      padding: theme.spacing.md,
    },
    eventsContainer: {
      paddingHorizontal: 0,
    },
    eventCard: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      backgroundColor: theme.colors.white,
      marginTop: theme.spacing.sm,
      marginHorizontal: 0,
      paddingVertical: theme.spacing.sm,
      paddingLeft: theme.spacing.md,
      paddingRight: theme.spacing.xs,
      borderRadius: 12,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.06,
            shadowRadius: 3,
            elevation: 2,
          }),
    },
    eventTouchable: {
      flex: 1,
      minWidth: 0,
    },
    eventInfo: {
      flex: 1,
    },
    eventTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: '#000000',
    },
    eventBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xxs,
      borderRadius: 16,
      borderWidth: 0.5,
      backgroundColor: theme.colors.white,
      borderColor: theme.colors.lightGrey,
      marginRight: theme.spacing.sm,
    },
    eventBadgeText: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.lightGrey,
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
    },
    eventDetails: {
      marginTop: 4,
    },
    eventDetailRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 6,
    },
    eventDetailText: {
      fontSize: 15,
      color: '#8E8E93',
    },
    eventActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    eventIconButton: {
      padding: theme.spacing.sm,
      borderRadius: 8,
    },
    eventCardDone: {
      opacity: 0.6,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: theme.colors.lightGrey,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxChecked: {
      borderColor: theme.colors.green,
      backgroundColor: theme.colors.green,
    },
    eventTitleDone: {
      textDecorationLine: 'line-through',
      color: theme.colors.lightGrey,
    },
    marginHorizontal: {
      marginHorizontal: theme.spacing.lg,
    },
  })
